<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useHeaderProps } from '@/common/customisation/composables';
import {
  useConversationsHeaderCustomisation,
  useConversationsHeaderTitleCustomisation,
  useConversationsNavIcon1Customisation,
  useConversationsNavIcon2Customisation,
} from './composables/useConversationsHeaderCustomisation';
import GenericHeader from '@/components/GenericHeader.vue';
import LoadingState from '@/components/loading/LoadingState.vue';
import ErrorState from '@/components/loading/ErrorState.vue';
import ChatItem from '@/components/chat/ChatItem.vue';
import { useConversationComments } from './composables/useConversationComments';
import { RequestLimitModalChoice, useConnectToAgentWizard } from '@/library/cf-v1/ConnectRequestEvent';
import { useMessageUtilities } from '@/library/message/useMessageUtilities';
import { useFlowEventHandlers } from '@/library/cf-v1/useFlowEventHandlers';
import { ConnectRequestType, isConnectToSupportEvent } from '@/library/support-wizard';
// TODO: move to common components
import ChatInput from '@/views/ChatWidget/components/ChatInput.vue';
import type { AttachmentSelected } from '@/views/ChatWidget/components/ChatInput.vue';
import LiveChatIndicator from '@/components/LiveChatIndicator/LiveChatIndicator.vue';
import VoiceCallIndicator from '@/components/VoiceCallIndicator/VoiceCallIndicator.vue';
import { PleasedApi } from '@/pleased-sdk';
import { createMediaMessage, createNotice, createUserSystemMessage, randomId } from '@/common/bot/lib';
import { ExternalBotEvent, MemberType, NoticeType, ResolutionType } from '@/common/bot/enum';
import { useI18n } from '@/plugins/i18n';
import PlsDialog from '@/components/PlsDialog.vue';
import { CallStatus, useTwilioVoiceCallManager } from '@/components/VoiceCallIndicator/useTwilioVoiceCallManager';
import TicketInfo from './components/TicketInfo.vue';
import { PleasedChatEvent } from '@/library/pleased-chat-controller/usePleasedChatController';
import { InteractionView, useChatWidget } from '@/stores/useChatWidget';
import * as SUtils from '@/common/customisation/utils/styleUtils';
import { useOverlayCustomisation } from '../ChatWidget/composables/useOverlayCustomisation';
import PlsButton1 from '@/components/PlsButton1.vue';
import { useEndChatButtonCustomisation } from '../ChatWidget/composables/useEndChatButtonCustomisation';
import { Bridge } from '@/common/bridge';
import { noop } from '@vueuse/core';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const chatWidgetStore = useChatWidget();

// Reset widget interaction view when component is unmounted
const previousWidgetInteractionView = chatWidgetStore.interactionView;
onUnmounted(() => chatWidgetStore.setInteractionView(previousWidgetInteractionView));

const conversation = useMessageUtilities();
const ticketId = computed(() => route.params.id as string | undefined);

// Get status from query parameter (passed from ConversationItem)
const ticketStatus = computed(() => (route.query.status as string) || '');

// Determine if conversation is closed or resolved based on status from query
const isClosedOrResolved = computed(() => {
  if (!ticketStatus.value) return false;
  const statusUpper = ticketStatus.value.toUpperCase();
  return statusUpper === 'RESOLVED' || statusUpper === 'CLOSED';
});

const {
  data: conversationData,
  isLoading: isLoadingComments,
  error: errorComments,
  refetch,
} = useConversationComments(
  ticketId,
  computed(() => !!ticketId.value),
  isClosedOrResolved
);

// Add comments to conversation
conversation.enqueue(refetch().then((data) => data.data?.comments ?? []));

const ticket = computed(
  () =>
    conversationData?.value?.ticket ?? {
      subject: '',
      status: '',
      commentCount: 0,
    }
);

const interactionView = ref(InteractionView.NONE);

const endVoiceData = ref<{ voiceChatId: number; status: CallStatus } | null>(null);
const isOpenConversation = computed(() => ticket?.value.status === 'NEW' || ticket?.value.status === 'OPEN');
const isVoiceCallEndDisabled = ref(false);
const isSelectingEndLiveChatResolution = ref(false);

// Refs
const contentContainerRef = ref<HTMLDivElement | null>(null);
const conversationInteractionRef = ref<HTMLDivElement | null>(null);
const interactionHeight = ref(0);

const {
  errorDialog,
  support,
  support: {
    chat: { onChat, chatMessages },
  },
  onRequestLimitModalResolved,
  onWizardEvent,
  startWizard,
  submitSelectedConnectOption,
} = useConnectToAgentWizard(conversation);

onRequestLimitModalResolved(async (choice) => {
  isSelectingEndLiveChatResolution.value = false;
  isVoiceCallEndDisabled.value = false;
  endVoiceData.value = null;
  interactionView.value = InteractionView.NONE;

  if (choice === RequestLimitModalChoice.CANCEL) {
    await refetch();
  }
});

const messages = computed(() => {
  const allMessages = [...conversation.messages.value, ...chatMessages.value];

  // Sort messages by timestamp in ascending order
  return allMessages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
});

const voiceChatId = computed(() => support.voice.voiceChatId.value);
const voiceChatDialog = computed(() => support.voice.voiceChatDialog.value);

const pleasedChat = computed(() => ({
  active: support.chat.active.value,
  chat: support.chat.chat.value,
  isClosed: support.chat.chat.value ? !support.chat.chat.value.isActive : false,
}));

const isLiveChatEndDisabled = computed(() => !pleasedChat.value.active || isSelectingEndLiveChatResolution.value);

const {
  submitAction,
  submitExternalAction,
  onConnectOptionSelected,
  onEndLiveChat,
  onEndVoiceCall,
  onEndAiChat,
  onEndChat,
} = useFlowEventHandlers({ conversation });
onConnectOptionSelected(submitSelectedConnectOption);

const scrollToBottom = () => {
  nextTick(() => {
    if (contentContainerRef.value) {
      contentContainerRef.value.scrollTop = contentContainerRef.value.scrollHeight;
    }
  });
};

const updateInteractionHeight = () => {
  nextTick(() => {
    if (conversationInteractionRef.value) {
      interactionHeight.value = conversationInteractionRef.value.offsetHeight;
      scrollToBottom();
    }
  });
};

watch(messages, () => {
  scrollToBottom();
});

watch(interactionView, () => {
  updateInteractionHeight();
});

watch(
  isOpenConversation,
  (newIsOpenConversation) => {
    if (newIsOpenConversation) {
      startWizard({
        connectToAgentConfig: {
          loginRequired: true,
          liveChat: true,
          openRequest: false,
          voiceChat: true,
          ticketId: ticketId.value,
        },
      });
    } else {
      // For non-open (resolved/closed) conversations the wizard never runs, so
      // the shared ChatWidget store is never reset from its default INPUT value.
      // Explicitly set it to NONE so ChatInteraction does not render a stray input bar.
      chatWidgetStore.setInteractionView(InteractionView.NONE);
    }
  },
  { immediate: true }
);

onWizardEvent((event) => {
  if (isConnectToSupportEvent(event) && event.payload.type === ConnectRequestType.FOLLOW_UP) {
    interactionView.value = InteractionView.INPUT;

    nextTick(() =>
      conversation.addItems(
        createNotice({
          type: NoticeType.GENERAL,
          message: t('notice.active_conversation.follow_up_wizard_hint'),
        })
      )
    );
  }
});

onChat(({ event }) => {
  if (event === PleasedChatEvent.CLOSED) {
    interactionView.value = InteractionView.END_CHAT_BUTTON;
    return;
  }

  interactionView.value = InteractionView.LIVE_CHAT_INDICATOR;
  conversation.addItems(
    createNotice({
      type: NoticeType.CHAT_WINDOW_DISCLAIMER,
      message: t('notice.active_conversation.chat_close_disclaimer'),
    })
  );
});

const headerCustomisation = useConversationsHeaderCustomisation();
const baseTitleCustomisation = useConversationsHeaderTitleCustomisation();
const navIcon1Customisation = useConversationsNavIcon1Customisation();
const navIcon2Customisation = useConversationsNavIcon2Customisation();
const search = {
  isVisible: false,
};

const overlayCustomisation = useOverlayCustomisation();
const contentContainerStyle = computed(() => ({
  ...SUtils.createBackgroundStyle(overlayCustomisation.backgroundColor),
  paddingBottom: `${interactionHeight.value + 16}px`,
}));

const { endChatCustomisation } = useEndChatButtonCustomisation();

const titleCustomisation = {
  textContent: computed(() =>
    isClosedOrResolved.value
      ? t('page.conversation_details.header_resolved_closed')
      : t('page.conversation_details.header_open')
  ),
  textColor: baseTitleCustomisation.textColor,
  isVisible: baseTitleCustomisation.isVisible,
};

const { headerProps } = useHeaderProps({
  header: headerCustomisation,
  title: titleCustomisation,
  navIcon1: navIcon1Customisation,
  navIcon2: navIcon2Customisation,
});

const { currentStatus, voiceCallDuration, hangUp } = useTwilioVoiceCallManager({
  voiceChatId: computed(() => voiceChatId.value as unknown as number),
  onConnected: () => {
    interactionView.value = InteractionView.VOICE_CHAT_INDICATOR;
  },
  onDisconnect: () => {
    interactionView.value = InteractionView.END_CHAT_BUTTON;
  },
});

// ChatInput handlers - todo move to tanstack + composable logic, remove from UI
const handleSubmit = (message: string) => {
  if (!ticketId.value) return;

  // Live chat message.
  if (pleasedChat.value.active) {
    return pleasedChat.value.chat?.sendMessage(message);
  }

  // Follow up comment.
  const newComment = PleasedApi.addTicketComment({ comment: message, ticketId: Number(ticketId.value) })
    .then((c) => {
      if (!c) return [createNotice({ type: NoticeType.ERROR, message: 'Failed adding a comment please try again.' })];
      return [
        createUserSystemMessage({ message }),
        createNotice({
          type: NoticeType.FOLLOW_UP_SUBMITTED,
          id: 'active-follow-up-notice',
          message: t('notice.active_conversation.post_followup') as string,
          isSticky: true,
        }),
      ];
    })
    .catch(() => [createNotice({ type: NoticeType.ERROR, message: 'Failed adding a comment please try again.' })]);

  conversation.enqueue(newComment, { memberKind: MemberType.VISITOR });
};

const handleFileUpload = (selectedAttachment: AttachmentSelected) => {
  if (!ticketId.value) return;

  const { file } = selectedAttachment;

  // Live chat attachment.
  if (pleasedChat.value.active) {
    return pleasedChat.value.chat?.sendFile(file);
  }

  // Follow up attachment.
  const newFile = PleasedApi.addAttachmentComment({ ticketId: Number(ticketId.value), file }).then(({ attachment }) =>
    createMediaMessage({
      attachment: {
        id: attachment.id,
        name: attachment.fileName,
        url: attachment.url,
        mimeType: attachment.contentType,
        contentType: attachment.contentType,
      },
      memberType: MemberType.VISITOR,
    })
  );

  conversation.enqueue(newFile, { memberKind: MemberType.VISITOR });
};

const handleTyping = (isTyping: boolean) => {
  pleasedChat.value.chat?.setTypingStatus(isTyping);
};

const handleEndLiveChat = async () => {
  isSelectingEndLiveChatResolution.value = true;
  submitExternalAction({ type: ExternalBotEvent.END_LIVE_CHAT, id: randomId() });
};

const handleEndChat = () => {
  router.replace('/new-home').catch(noop);
  Bridge.sendToParent('CLOSE');
};

const handleVoiceCallDisconnect = () => {
  isVoiceCallEndDisabled.value = true;
  endVoiceData.value = { voiceChatId: voiceChatId.value!, status: currentStatus.value };
  submitExternalAction({ type: ExternalBotEvent.END_VOICE_CALL, id: randomId() });
};

onEndAiChat(() => {
  interactionView.value = InteractionView.END_CHAT_BUTTON;
});

onEndChat(() => {
  router.replace('/conversations').catch(noop);
});

onEndLiveChat(({ resolution }) => {
  if (resolution === ResolutionType.YES) {
    pleasedChat.value.chat?.end();
  } else if (resolution === ResolutionType.NO) {
    isSelectingEndLiveChatResolution.value = false;
  }
});

onEndVoiceCall(({ resolution }) => {
  if (resolution === ResolutionType.YES) {
    if (!(endVoiceData.value?.status === CallStatus.CONNECTED)) {
      handleEndChat();
      return;
    }

    hangUp();
    interactionView.value = InteractionView.END_CHAT_BUTTON;
  } else if (resolution === ResolutionType.NO) {
    isVoiceCallEndDisabled.value = false;
  }

  endVoiceData.value = null;
});
</script>

<template>
  <div class="page-container">
    <GenericHeader
      :header="headerProps.header"
      :title="headerProps.title"
      :nav-icon1="headerProps.navIcon1"
      :nav-icon2="headerProps.navIcon2"
      :search="search"
    />

    <!-- Content Container - Scrollable -->
    <div ref="contentContainerRef" class="content-container page-content" :style="contentContainerStyle">
      <TicketInfo v-if="isClosedOrResolved" :ticket="ticket" />

      <LoadingState v-if="isLoadingComments" />
      <ErrorState v-else-if="errorComments" message="Failed to load conversation details" />

      <div v-else-if="conversationData" class="conversation-container">
        <!-- Live Chat Indicator - Fixed position when live chat is active -->
        <div v-if="interactionView === InteractionView.LIVE_CHAT_INDICATOR" class="live-chat-indicator-wrapper">
          <LiveChatIndicator :disabled="isLiveChatEndDisabled" @end-chat="handleEndLiveChat" />
        </div>

        <div class="conversation-messages">
          <div v-for="(m, idx) in messages" :key="`${m.id}`">
            <ChatItem
              :item="m"
              :previous="messages[idx - 1]"
              :next="messages[idx + 1]"
              @message:input="submitAction"
              @external-event="submitExternalAction"
            />
          </div>
        </div>
      </div>
    </div>

    <div ref="conversationInteractionRef" class="conversation-interaction">
      <!-- Chat Input - Fixed at bottom when showChatInput is true -->
      <div v-if="interactionView === InteractionView.INPUT || interactionView === InteractionView.LIVE_CHAT_INDICATOR">
        <ChatInput
          :is-opened="true"
          :show-file-upload="true"
          :show-refresh="false"
          @submit-message="handleSubmit"
          @file-upload="handleFileUpload"
          @typing="handleTyping"
        />
      </div>

      <!-- Voice Call Indicator - Fixed position when voice call is active -->
      <div v-if="interactionView === InteractionView.VOICE_CHAT_INDICATOR" class="voice-call-indicator-wrapper">
        <VoiceCallIndicator
          :call-duration="voiceCallDuration"
          :disabled="isVoiceCallEndDisabled"
          @end-call="handleVoiceCallDisconnect"
        />
      </div>

      <div v-if="interactionView === InteractionView.END_CHAT_BUTTON" class="end-chat-button-container">
        <PlsButton1
          :text="t('global.end_chat')"
          :background-color="endChatCustomisation.backgroundColor"
          :text-color="endChatCustomisation.textColor"
          :border-color="endChatCustomisation.borderColor"
          class="end-chat-button"
          @click="handleEndChat"
        />
      </div>
    </div>

    <PlsDialog v-if="errorDialog?.open" v-bind="errorDialog" />
    <PlsDialog v-if="voiceChatDialog?.open" v-bind="voiceChatDialog" />
  </div>
</template>

<style scoped lang="scss">
.content-container {
  overflow-y: auto;
}

.conversation-container {
  padding: 16px;
}

.conversation-interaction {
  position: fixed;
  bottom: 0;

  width: 100%;
  z-index: 99;
}

.live-chat-indicator-wrapper {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 98;
}

.end-chat-button-container {
  display: flex;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.end-chat-button {
  width: auto;
  max-width: min(40vw, 200px);
  min-width: min(40vw, 200px);

  @media (min-width: 768px) {
    max-width: 200px;
  }
}
</style>
